/**
 * Watchlist.jsx — dashboard/Watchlist.jsx
 *
 * Features:
 *   - Click row to select symbol → drives chart; also spawns detail+pivot panels
 *   - Drag rows to reorder (pointer events)
 *   - Pre-market volume, bid/ask spread columns
 *   - AI direction tooltip removed; direction shown in DetailPanel instead
 */

import { useState } from 'react';
import { classifyPreMarketVolume, spread } from '../../lib/indicators';
import { fmtPrice, fmtPct, colorForPct } from '../../lib/format';

// ── Drag-reorder hook ─────────────────────────────────────────────────────────
function useDragReorder(symbols, onReorder) {
  const dragIdx = { current: null };

  const onDragStart = (idx) => { dragIdx.current = idx; };
  const onDragOver  = (e, idx) => {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === idx) return;
    const next = [...symbols];
    const [item] = next.splice(dragIdx.current, 1);
    next.splice(idx, 0, item);
    dragIdx.current = idx;
    onReorder(next);
  };
  const onDragEnd = () => { dragIdx.current = null; };

  return { onDragStart, onDragOver, onDragEnd };
}

// ── Main Watchlist ─────────────────────────────────────────────────────────────
export default function Watchlist({ symbols, quotesBySym, onAdd, onRemove, onSelect, onReorder, onOpenChart, onOpenDetail, selected }) {
  const [input, setInput] = useState('');
  const { onDragStart, onDragOver, onDragEnd } = useDragReorder(symbols, onReorder);

  const submit = (e) => {
    e.preventDefault();
    const v = input.trim().toUpperCase();
    if (v) { onAdd(v); setInput(''); }
  };

  const handleRowClick = (sym) => {
    onSelect(sym);
    onOpenDetail && onOpenDetail(sym);   // spawn detail + pivot panels
  };

  return (
    <div className="wl-wrap">
      <div className="wl-scroll">
        {symbols.length === 0
          ? <div className="empty">Add a ticker below to begin.</div>
          : (
            <table className="wl-table">
              <thead>
                <tr>
                  <th style={{ width: 20 }}></th>
                  <th>Symbol</th>
                  <th>Last</th>
                  <th>Chg%</th>
                  <th>Pre%</th>
                  <th>PreVol</th>
                  <th>Sprd%</th>
                  <th title="Open 1-min chart">⌘</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {symbols.map((sym, idx) => {
                  const q   = quotesBySym[sym];
                  const pmv = classifyPreMarketVolume(q?.preMarketVolume, q?.averageDailyVolume3Month);
                  const sp  = spread(q?.bid, q?.ask);
                  const isSelected = sym === selected;

                  return (
                    <tr
                      key={sym}
                      draggable
                      onDragStart={() => onDragStart(idx)}
                      onDragOver={(e) => onDragOver(e, idx)}
                      onDragEnd={onDragEnd}
                      onClick={() => handleRowClick(sym)}
                      className={isSelected ? 'selected' : ''}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* Drag handle */}
                      <td
                        style={{ color: 'var(--faint)', fontSize: 12, cursor: 'grab', userSelect: 'none', textAlign: 'center', padding: '7px 4px' }}
                        onClick={e => e.stopPropagation()}
                      >⋮⋮</td>

                      <td style={{ fontWeight: 600 }}>
                        {sym}
                        {q?.shortName && <span className="sym-name">{q.shortName}</span>}
                      </td>

                      <td>{fmtPrice(q?.regularMarketPrice)}</td>
                      <td style={{ color: colorForPct(q?.regularMarketChangePercent) }}>{fmtPct(q?.regularMarketChangePercent)}</td>
                      <td style={{ color: colorForPct(q?.preMarketChangePercent) }}>{fmtPct(q?.preMarketChangePercent)}</td>
                      <td><span className={`badge ${pmv.level}`}>{pmv.level}</span></td>
                      <td>{sp ? <span className="faint">{sp.pct.toFixed(3)}%</span> : <span className="faint">—</span>}</td>

                      <td>
                        <button
                          title={`Open 1-min chart for ${sym}`}
                          onClick={e => { e.stopPropagation(); onOpenChart && onOpenChart(sym); }}
                          style={{
                            background: 'none', border: '1px solid var(--border)',
                            color: 'var(--faint)', borderRadius: 3,
                            padding: '1px 5px', cursor: 'pointer', fontSize: 11,
                          }}
                          onMouseEnter={e => { e.currentTarget.style.color='var(--accent)'; e.currentTarget.style.borderColor='var(--accent)'; }}
                          onMouseLeave={e => { e.currentTarget.style.color='var(--faint)'; e.currentTarget.style.borderColor='var(--border)'; }}
                        >⌘</button>
                      </td>

                      <td>
                        <button
                          className="wl-remove"
                          onClick={e => { e.stopPropagation(); onRemove(sym); }}
                          title={`Remove ${sym}`}
                        >×</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        }
      </div>

      {/* Add ticker */}
      <form onSubmit={submit} className="wl-add">
        <input
          value={input}
          onChange={e => setInput(e.target.value.toUpperCase())}
          placeholder="Add ticker…"
          className="wl-input"
          spellCheck={false}
        />
        <button type="submit" className="wl-btn">+</button>
      </form>
    </div>
  );
}
